'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import type { ButtonProps } from '@packages/shared/src/ui/Button/Button';
import { ButtonLink } from '@packages/shared/src/ui/Button/Button';
import { useRef } from 'react';
import type { DrupalIconTypes, Size } from '../../../interfaces';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { SoftHyphenInjector } from '../SoftHyphenInjector';
import type { Icon, LinkByButtonType } from './types';

const getIcon = (icon: Icon, size: Size) => {
  const icons = {
    ArrowRight: {
      small: <span className="icon-emp-arrow-mini-right" />,
      medium: <span className="icon-emp-arrow-medium-right size-3!" />,
      large: <span className="icon-emp-arrow-large-right size-3!" />,
    },
    ArrowLeft: {
      small: <span className="icon-emp-arrow-mini-left" />,
      medium: <span className="icon-emp-arrow-medium-left size-3!" />,
      large: <span className="icon-emp-arrow-large-left size-3!" />,
    },
  };

  return icons[icon][size];
};

const colors = [
  'primary',
  'secondary',
  'error',
  'warning',
  'success',
  'sale',
  'additional',
  'drupal',
  'default',
] as const;

export const LinkByButton = ({
  data,
  icon,
  className,
}: {
  data: LinkByButtonType;
  icon?: DrupalIconTypes;
  className?: string;
}) => {
  const {
    field_link: link,
    field_text: text,
    field_layout: color,
    behavior_paragraph_layout: behaviorParagraphLayout,
    behavior_paragraph_size: behaviorSize,
    field_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
  } = data;
  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;
  const finalColor = (behaviorParagraphLayout || color) as ButtonProps['color'];

  const iconElement = icon && behaviorSize && getIcon(icon, behaviorSize);
  const iconPosition = icon?.startsWith('ArrowRight') ? 'endIcon' : 'startIcon';

  const tmpLink = buildLinkWithArguments(link, data);
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const { trackClick } = useTrackMarketing(data, buttonRef, finalLink);

  return (
    <ButtonLink
      className={className}
      ref={buttonRef}
      LinkComponent={Link}
      href={finalLink}
      // runtime-check if value is in type values
      {...(finalColor && colors.includes(finalColor) && { color: finalColor })}
      {...(behaviorSize && {
        size: behaviorSize,
      })}
      {...(linkTarget && linkTarget === '_blank' && { target: linkTarget })}
      rel={linkRel}
      onClick={() => trackClick()}
    >
      {iconPosition === 'startIcon' && iconElement} <SoftHyphenInjector text={text} />
      {iconPosition === 'endIcon' && iconElement}
    </ButtonLink>
  );
};
